<template>
  <div :id="uuid" class="media-transcription" :class="{ loading: isLoading }">
    <inline-loader v-if="isLoading" />
    <ul v-if="!isLoading" class="non-highlightable transcript-list">
      <li
        v-for="(cue, index) in viewableTrackCues"
        :id="uuid + '_' + cue.id"
        :key="uuid + '_' + cue.id"
        :class="cueClass(cue.id)"
        @click="handlePanToStart(cue.startTime)"
      >
        <cv-tag :label="timeStamp(cue.startTime)" kind="blue" />
        <span v-if="!canEditTranscript" class="transcript-label">
          <span :class="cue.transcriptLabelClasses">{{ cue.text }}</span>
        </span>
        <text-area
          v-else
          class="transcript-input"
          :character-limit="transcriptCharacterLimit"
          :error-message="errorTitle"
          :text-id="cue.id"
          :value="cue.text"
          @input="handleTextChange($event, index)"
        />
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch, nextTick } from "vue";
import { MediaPlayerEventBus } from "@plugins/media/service/media.player.event.bus";
import { CHARACTER_LIMIT } from "../media.constant";
import mediaUtils from "@plugins/media/media.utils";
import RandomUtils from "@utils/random.utils";
import ScrollUtils from "@utils/scroll.utils";
import { isEmpty } from "@utils/var.check";
import { AnnotationEventBus } from "@modules/annotation/service/annotation.event.bus";
import TextArea from "@plugins/common/components/TextArea.vue";
import InlineLoader from "@plugins/common/components/InlineLoader.vue";
import { HighlightInfo, TranscriptCue } from "@plugins/media/types.ts";
import { useEventBusWatch } from "@plugins/common/composables/event-bus-watch.ts";
import { validateMaxCharacterLimit } from "@plugins/common/utils/text-input-validation-helper.ts";

const props = withDefaults(
  defineProps<{
    captionSrc: string;
    isVideo?: boolean;
    canEditTranscript?: boolean;
    transcriptStart?: number;
    transcriptEnd?: number;
    annotatable?: boolean;
    mediaUuid?: string;
    anchor?: string;
    showHighlightedCuesOnly?: boolean;
    showHighlightOnMedia?: boolean;
    annotationUuid?: string;
    isFromSubpage?: boolean;
  }>(),
  {
    isVideo: false,
    canEditTranscript: false,
    transcriptStart: undefined,
    transcriptEnd: undefined,
    annotatable: false,
    mediaUuid: "",
    anchor: "",
    showHighlightedCuesOnly: false,
    showHighlightOnMedia: false,
    annotationUuid: undefined,
    isFromSubpage: false,
  },
);

const emit = defineEmits<{
  (e: "transcriptEdited", payload: { value: string; index: number }): void;
  (e: "trackLoaded", cues: TextTrackCueList): void;
}>();

const { trackWatch } = useEventBusWatch();

const isLoading = ref(true);
const track = ref<TextTrack>();
const activeCues = ref<string>();
const isScrollRequired = ref(false);
const transcriptCharacterLimit = CHARACTER_LIMIT.TRANSCRIPT;
const errorTitle = ref("");
const disableScrollForClick = ref(false);
const uuid = ref(RandomUtils.randomUuid());
const highlightedTimestamp = ref<HighlightInfo[]>([]);
const scrollWhenLoaded = ref(false);

const isActive = (id: string): boolean => activeCues.value === id;

const cueClass = (id: string) => ({
  cue: true,
  active: isActive(`${uuid.value}_${id}`),
});

const viewableTrackCues = computed<TranscriptCue[]>(() => {
  const cuesList = track.value?.cues;
  if (!cuesList) return [];

  if (
    (props.transcriptStart == null || props.transcriptEnd == null) &&
    isEmpty(highlightedTimestamp.value)
  ) {
    const list: TranscriptCue[] = [];
    for (const element of cuesList) {
      const c = element as VTTCue;
      const cue = new VTTCue(c.startTime, c.endTime, c.text) as TranscriptCue;
      cue.id = (c as VTTCue).id;
      cue.transcriptLabelClasses = [];
      list.push(cue);
    }
    return list;
  }

  const result: TranscriptCue[] = [];
  for (const element of cuesList) {
    const c = element as VTTCue;
    if (isCueViewable(c)) {
      const newCue = new VTTCue(c.startTime, c.endTime, c.text) as TranscriptCue;
      newCue.id = c.id;
      newCue.transcriptLabelClasses = [];

      let isHighlighted = false;
      highlightedTimestamp.value.forEach((timestamp) => {
        const time = timestamp.timestamp / 1000;
        if (
          time >= newCue.startTime &&
          time <= newCue.endTime &&
          !newCue.transcriptLabelClasses.includes(timestamp.highlighterClass)
        ) {
          newCue.transcriptLabelClasses.push(timestamp.highlighterClass);
          isHighlighted = true;
        }
      });

      if (props.showHighlightedCuesOnly) {
        if (isHighlighted) result.push(newCue);
      } else {
        result.push(newCue);
      }
    }
  }
  return result;
});

watch(activeCues, () => {
  if (isScrollRequired.value && !disableScrollForClick.value) {
    if (activeCues.value) scrollToView(activeCues.value);
  }
  disableScrollForClick.value = false;
});

watch(
  () => props.captionSrc,
  () => loadTrack(),
);

onMounted(() => {
  loadTrack();
});

const onActiveCues = (data: { isFromSubpage?: boolean; captionSrc: string; cueId?: string }) => {
  isScrollRequired.value = !data.isFromSubpage;
  if (props.captionSrc === data.captionSrc && data.cueId) {
    handleActiveCues(data.cueId);

    if (isLoading.value) {
      scrollWhenLoaded.value = true;
    } else {
      scrollToView(activeCues.value as string);
    }
  }
};

const getCurrentCueText = (timestamp: number): string => {
  let activeText = "";
  viewableTrackCues.value.forEach((cue) => {
    if (timestamp >= cue.startTime && timestamp <= cue.endTime) {
      activeText = cue.text;
    }
  });
  return activeText;
};

const onRenderTimestamp = (data: {
  isFromSubpage?: boolean;
  anchor?: string;
  uuid?: string;
  annotationList?: { timestamp: number; highlighterClass: string }[];
}) => {
  if (isEmpty(data)) return;
  if (props.isFromSubpage !== (data.isFromSubpage === true)) return;
  if (
    (props.showHighlightedCuesOnly && props.annotationUuid !== data.uuid) ||
    (!props.showHighlightedCuesOnly && props.anchor !== data.anchor)
  ) {
    return;
  }
  populateHighlightInfo(data);
};

trackWatch(MediaPlayerEventBus.$on("activeCues", onActiveCues));
if (props.annotatable || props.showHighlightOnMedia) {
  trackWatch(AnnotationEventBus.$on("renderTimestampAnnotation", onRenderTimestamp));
}

function scrollToView(cueId: string): void {
  const mediaTranscriptContainer = document.getElementById(uuid.value);
  const activeLine = document.getElementById(cueId);

  if (isEmpty(activeLine)) {
    return;
  }

  ScrollUtils.scrollToTopOfContainerWithHeaderOffset(activeLine, mediaTranscriptContainer, -48);
}

function validate() {
  for (const cue of track.value.cues) {
    const text = cue.text;
    if (!validateCharacterLimit(text)) return false;
  }
  return true;
}

function validateCharacterLimit(transcript) {
  let isValid = true;
  if (!validateMaxCharacterLimit(transcript, transcriptCharacterLimit)) {
    isValid = false;
  }
  return isValid;
}

function handlePanToStart(startTime: number): void {
  disableScrollForClick.value = true;
  MediaPlayerEventBus.$emit("panToPoint", {
    time: startTime,
    pauseMedia: false,
    uuid: props.mediaUuid,
  });
}

function loadTrack(): void {
  isLoading.value = true;
  const trackElement = document.createElement("track");
  const element = props.isVideo ? document.createElement("video") : document.createElement("audio");
  element.setAttribute("crossorigin", "use-credentials");
  trackElement.setAttribute("crossorigin", "use-credentials");
  trackElement.src = props.captionSrc;
  track.value = trackElement.track;

  trackElement.onload = () => {
    const cuesList = track.value?.cues;
    if (!cuesList) return;
    for (const element of cuesList) {
      const cue = element as VTTCue;
      cue.startTime = Math.ceil(cue.startTime * 1000) / 1000;
    }
  };

  element.append(trackElement);
  if (track.value) track.value.mode = "hidden";

  setTimeout(() => {
    isLoading.value = false;

    nextTick(() => {
      if (track.value?.cues) emit("trackLoaded", track.value.cues);
      if (scrollWhenLoaded.value && activeCues.value) {
        scrollToView(activeCues.value);
        scrollWhenLoaded.value = false;
      }
    });
  }, 1000);
}

function handleActiveCues(index: string): void {
  activeCues.value = `${uuid.value}_${index}`;
}

function handleTextChange(value: string, textIndex: number): void {
  errorTitle.value = "";
  emit("transcriptEdited", { value, index: textIndex });
}

function timeStamp(startTime: number): string {
  return mediaUtils.formatToVideoTimestamp(startTime);
}

function isCueViewable(cue: VTTCue): boolean {
  if (props.transcriptStart == null || props.transcriptEnd == null) {
    return true;
  }
  return (
    (cue.startTime >= props.transcriptStart && cue.startTime < props.transcriptEnd) ||
    (cue.endTime > props.transcriptStart && cue.endTime <= props.transcriptEnd) ||
    (cue.endTime >= props.transcriptEnd && cue.startTime <= props.transcriptStart)
  );
}

function populateHighlightInfo(data: {
  annotationList?: { timestamp: number; highlighterClass: string }[];
}): void {
  highlightedTimestamp.value = [];
  if (!isEmpty(data.annotationList)) {
    data.annotationList!.forEach((annotation) => {
      highlightedTimestamp.value.push({
        timestamp: annotation.timestamp,
        highlighterClass: annotation.highlighterClass,
      });
    });
  }
}

defineExpose({
  getCurrentCueText,
  validate,
});
</script>

<style lang="scss">
@import "@styles/abstracts/functions";
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

$transcript-height-desktop: 11rem; // 5 lines
$transcript-height-mobile: 17rem; // 8 lines
$transcript-timestamp-width: 3.5rem;

.media-transcription {
  position: relative;
  padding: $spacing-02 0;
  width: 100%;
  &.loading {
    // set minimum height to display inline-loader
    min-height: $transcript-height-desktop;
  }
  .attachment-placeholder ~ & {
    // [consumption] transcription is within richtext, so need to overwrite some property
    border-top: $divider-01;
    overflow-y: auto;
    padding: $spacing-03 $spacing-02; // overwrite padding
    max-height: $transcript-height-desktop;
    @include media("mobile") {
      max-height: $transcript-height-mobile;
    }
    > ul {
      &.transcript-list {
        list-style-type: none; // overwrite list-style-type of `.rich-text-editor .mce-content-body ul`
        padding: 0; // overwrite padding-left of `.rich-text-editor .mce-content-body ul`
      }
    }
  }
  > ul {
    &.transcript-list {
      display: flex;
      flex-flow: column;
      gap: 0; // no additional space between each transcript row, li padding is suffice
      > li {
        &.cue {
          display: flex;
          align-items: flex-start;
          gap: $spacing-03; // space between timestamp and caption
          padding: $spacing-02; // space between highlight and timestamp + caption
          border-radius: 10px;
          &.active {
            background: $hover-ui;
          }
          .bx--tag {
            @include carbon--type-style("label-01");
            display: block;
            margin: 0;
            padding: $spacing-02 $spacing-04;
            min-width: $transcript-timestamp-width;
            max-width: $transcript-timestamp-width;
            background-color: tint($interactive-01, 80%);
            color: $tag-text-01;
          }
          > .transcript-label {
            @include carbon--type-style("body-short-01", true);
            flex-grow: 1;
          }
          > .transcript-input {
            flex-grow: 1;
            .bx--text-area {
              @include carbon--type-style("body-short-01", true);
              background-color: $ui-background;
            }
          }
        }
      }
    }
  }
}
</style>
